import _toConsumableArray from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import dayjs from 'dayjs';
import { isEqual, isEqualWith, isNull, isUndefined, orderBy } from 'lodash';
import { getBrokerQuotingThroughMap } from '../constants';
import { BUSINESS_INTERUPTION, BUSINESS_OWNERS_POLICY_COVER, CMR_COVER, COMPUTER_BREAKDOWN_COVER, CYBER_LIABILITY_COVER, DIRECTORS_OFFICERS_COVER, DRIVERS_NEGLIGENCE, EMPLOYERS_LIABILITY, EMPLOYMENT_PRACTICES_COVER, FIDELITY_CRIME, GOODS_IN_TRANSIT, MONEY_ASSAULT_COVER, PACKAGE_COVER, PORTABLE_EQUIPMENT_COVER, PROFESSIONAL_INDEMNITY, PROPERTY_COVER_TYPE_LIST, PUBLIC_LIABILITY } from '../constants/coverage';
import { AmericanCountryList, EuropeanUnionList, PROVIDER_SOURCE, UKCountryList } from '../constants/quote';
import { BusinessTypeNotSelectedOther, GoodsInTransitKey, getBusinessTypeList, getGreatAmericanBusinessInsuranceBusinessTypeList, getGreatAmericanBusinessInsuranceOtherBusinessTypeList, getGreateAmericanCoverMap, getOtherBusinessTypeList, goodsInTransitOldCompatibilityKey } from '../localeConstants';
import { formatCurrency } from '../shared/formatters';
import { InsuranceType, PromotionOfferType, QuoteSourceEnum, QuoteStatus } from '../shared/generated';
import { multiplyNumber } from './decimal';
import { formatCommonDateStr, judgeWhetherIsEarlierThanTheDate, judgeWhetherIsLaterThanTheDate, judgeWhetherIsSameThanTheDate } from './time';
/**
 * Insurance info
 */

export var getQuoteCurrentInsuranceInfo = function getQuoteCurrentInsuranceInfo(data) {
  var _ref = data || {},
      insuranceType = _ref.insuranceType,
      providerCode = _ref.providerCode,
      country = _ref.country,
      temporaryUseFormRecruitmentOpenFromValidation = _ref.temporaryUseFormRecruitmentOpenFromValidation;

  var info = {};
  var code = providerCode;

  if (code) {
    info.isGit = code === PROVIDER_SOURCE().ACCELERANT_GIT.value;
    info.isChubb = code === PROVIDER_SOURCE().CHUBB.value;
    info.isAccelerant = code === PROVIDER_SOURCE().ACCELERANT.value;
    info.isHiscoxUS = code === PROVIDER_SOURCE().HISCOX_US.value;
    info.isMsAmlin = code === PROVIDER_SOURCE().MSAMLIN.value;
    info.isSagic = code === PROVIDER_SOURCE().SAGIC.value;
    info.isGreateAmerican = code === PROVIDER_SOURCE().GREATEAMERICAN.value;
    info.isRecruitment = code === PROVIDER_SOURCE().RECRUITMENT.value;
    info.isGreateAmericanBussinessInsurance = code === PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value; // info.isBroker = code === PROVIDER_SOURCE().BROKER.value;

    info.isUnknownInsurance = !(info.isChubb || info.isAccelerant || info.isHiscoxUS || info.isMsAmlin || info.isSagic || info.isGreateAmerican || info.isBroker || info.isRecruitment || info.isGreateAmericanBussinessInsurance);
  } else {
    if (insuranceType === InsuranceType.GOODS_IN_TRANSIT_INSURANCE) {
      if (country === 'US') {
        info.isGreateAmerican = true;
      }
    }
  }

  if (temporaryUseFormRecruitmentOpenFromValidation) {
    info = {
      isRecruitment: true
    };
  }

  return info;
};
/**
 * Contract period
 */

export var ContractPeriodMaxAllowMonth = 9;
export var ContractPeriodMinAllowMonth = 3;
export var ContractPeriodSubtractedDays = 1; // Allow min end date

var getEndDateAllowMinDate = function getEndDateAllowMinDate(startD) {
  return dayjs(startD).add(ContractPeriodMinAllowMonth, 'month').subtract(ContractPeriodSubtractedDays, 'day');
}; // Modify the end time to an integer month to start time


var modifyEndTimeIntegerMonth = function modifyEndTimeIntegerMonth(startTime, endTime) {
  var startDays = startTime.getDate();
  var endDays = endTime.getDate(); // Diff month

  var monthDiff = (endTime.getFullYear() - startTime.getFullYear()) * 12 + (endTime.getMonth() - startTime.getMonth());
  return dayjs(startTime).add(endDays <= startDays ? monthDiff : monthDiff + 1, 'month').subtract(ContractPeriodSubtractedDays, 'day').toDate();
};

export var getEndDateWhenStartTimeChange = function getEndDateWhenStartTimeChange(date, endDate) {
  // end <= start || end - start < 3 month
  if (judgeWhetherIsLaterThanTheDate(date, endDate) || judgeWhetherIsSameThanTheDate(date, endDate) || judgeWhetherIsEarlierThanTheDate(endDate, getEndDateAllowMinDate(date))) {
    return getEndDateAllowMinDate(date).toDate();
  } else {
    return modifyEndTimeIntegerMonth(date, endDate);
  }
};
export var getEndDateWhenEndTimeChange = function getEndDateWhenEndTimeChange(date, startDate) {
  // end < start + 3 month
  if (judgeWhetherIsEarlierThanTheDate(date, getEndDateAllowMinDate(startDate))) {
    return getEndDateAllowMinDate(startDate).toDate();
  } else {
    return modifyEndTimeIntegerMonth(startDate, date);
  }
}; // Rate and total estimated income

export var calculateRateAndTotalIncomeValue = function calculateRateAndTotalIncomeValue(data) {
  var perType = data.perType,
      projectPeriodEnd = data.projectPeriodEnd,
      projectPeriodStart = data.projectPeriodStart,
      perCount = data.perCount,
      rateValue = data.rateValue; // Contract days

  var contractDays = Math.ceil((new Date(projectPeriodEnd).getTime() - new Date(projectPeriodStart).getTime()) / (1000 * 60 * 60 * 24));
  var worksCount = 0;

  if (perType === 'perDay') {
    worksCount = Math.ceil(perCount / 7 * contractDays);
  } else if (perType === 'perWeek') {
    // 365 / 7 ~= 52
    worksCount = Math.ceil(perCount / 52 * contractDays);
  } else if (perType === 'perMonth') {
    worksCount = Math.ceil(perCount / 12 * contractDays);
  }

  return worksCount * Number(rateValue || 0);
}; // Expiry date

export var getCoverageExpiryDate = function getCoverageExpiryDate(item) {
  return "".concat(item !== null && item !== void 0 && item.start_date ? formatCommonDateStr(item.start_date) : 'N/A', " - ").concat(item !== null && item !== void 0 && item.end_date ? formatCommonDateStr(item.end_date) : 'N/A');
};
export var judgeWhetherTheInsuranceTypeIsSame = function judgeWhetherTheInsuranceTypeIsSame(insuranceType1, insuranceType2) {
  var gitIsSame = (insuranceType1 === GoodsInTransitKey ? insuranceType2 === goodsInTransitOldCompatibilityKey : false) || (insuranceType1 === goodsInTransitOldCompatibilityKey ? insuranceType2 === GoodsInTransitKey : false);
  return insuranceType1 === insuranceType2 || gitIsSame;
};
export var judgeWhetherQuoteDataHasChanged = function judgeWhetherQuoteDataHasChanged(oldQuoteData, newQuoteData) {
  if (!oldQuoteData && !newQuoteData) return false;
  if (!oldQuoteData && !!newQuoteData) return true;
  var compareFieldList = ['firstName', 'lastName', 'email', 'phone', 'country', 'projectPeriodStart'];
  var providerCode = newQuoteData.providerCode;

  switch (providerCode) {
    case PROVIDER_SOURCE().ACCELERANT.value:
      compareFieldList = [].concat(_toConsumableArray(compareFieldList), ['businessType', 'companyName', 'businessAddress', 'detailedAddress', 'businessCity', 'businessPostcode', 'registrationNumber', 'taxNumber', 'companyIncorporationDate', 'policyType', 'retroactiveDate', 'businessActivity', 'turnoverRaw', 'incomeOrigin', 'coverType', 'covers', 'elSize', 'hasClaims', 'claimList', 'declarationQuestions', 'r2ROption', 'propertyActivities', 'propertyList', 'accountancyActivityList', 'portableEquipmentInsured', 'businessInterruptionInsured']);
      break;

    case PROVIDER_SOURCE().RECRUITMENT.value:
      compareFieldList = [].concat(_toConsumableArray(compareFieldList), ['businessType', 'companyName', 'businessAddress', 'detailedAddress', 'businessCity', 'businessPostcode', 'registrationNumber', 'taxNumber', 'companyIncorporationDate', 'businessActivity', 'recruitmentBusinessType', 'turnoverRaw', 'recruitmentSplitPercent', 'payrollSplitPercent', 'incomeOrigin', 'employees', 'payrollForPlacedCandidate', 'permanentSplitPercentForPlacedCandidate', 'temporarySplitPercentForPlacedCandidate', 'placementTypeAndSplitList', 'coverType', 'covers', 'hasClaims', 'claimList', 'declarationQuestions', 'declarationComment', 'propertyList', 'portableEquipmentInsured', 'businessInterruptionInsured']);
      break;

    case PROVIDER_SOURCE().ACCELERANT_GIT.value:
      compareFieldList = [].concat(_toConsumableArray(compareFieldList), ['businessType', 'birthDate', 'companyName', 'businessAddress', 'detailedAddress', 'businessCity', 'businessPostcode', 'registrationNumber', 'taxNumber', 'companyIncorporationDate', 'businessActivity', 'insuranceBundle', 'employees', 'vehicleList']);
      break;

    case PROVIDER_SOURCE().HISCOX_US.value:
      compareFieldList = [].concat(_toConsumableArray(compareFieldList), ['businessType', 'companyName', 'businessAddress', 'businessPostcode', 'businessCity', 'businessCounty', 'businessState', 'companyIncorporationDate', 'businessActivity', 'turnoverRaw', 'similarInsurance', 'declarationQuestions', 'estSalesLargestProject', 'estSalesLargestCustmr1', 'haveCyberInsurance', 'cyberRetroactiveDateAvailable', 'cyberRetroactiveDate', 'mediaLiabilityRetroactiveDateAvailable', 'mediaLiabilityRetroactiveDate', 'haveProfLiabilityInsurance', 'professionalLiabilityRetroactiveDateAvailable', 'professionalLiabilityRetroactiveDate', 'professionalLicenseAttestation', 'extraDeclarationSelectOptionList', 'estmtdPayrollExpense', 'squareFeetOccupiedByYou', 'numOfOwners', 'limitedEmployeeCount', 'employees']);
      break;

    default:
      break;
  }

  var compareOldQuoteData = {};
  var compareNewQuoteData = {};
  compareFieldList.forEach(function (key) {
    compareOldQuoteData[key] = oldQuoteData[key];
    compareNewQuoteData[key] = newQuoteData[key];
  });
  var isSame = isEqualWith(compareOldQuoteData, compareNewQuoteData, function (obj1, obj2) {
    if ((isNull(obj1) || isUndefined(obj1)) && isNull(obj2) || isUndefined(obj2)) {
      return true;
    }

    return undefined;
  });

  if (!isSame) {
    compareFieldList.forEach(function (key) {
      if (!isEqual(compareOldQuoteData[key], compareNewQuoteData[key])) {
        console.log('Auto save not same: ', key);
      }
    });
  }

  return !isSame;
};
export var getQuoteBusinessTypeList = function getQuoteBusinessTypeList(providerCode, config) {
  if ([PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value, PROVIDER_SOURCE().HISCOX_US.value].includes(providerCode)) {
    return [].concat(_toConsumableArray(getGreatAmericanBusinessInsuranceBusinessTypeList()), [{
      value: BusinessTypeNotSelectedOther,
      label: 'Other',
      displayLabel: 'Other'
    }]);
  } else {
    return getBusinessTypeList(config);
  }
};
export var getQuoteOtherBusinessTypeList = function getQuoteOtherBusinessTypeList(providerCode, config) {
  if ([PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value, PROVIDER_SOURCE().HISCOX_US.value].includes(providerCode)) {
    return getGreatAmericanBusinessInsuranceOtherBusinessTypeList();
  } else {
    return getOtherBusinessTypeList(providerCode, config);
  }
};
export var quoteIsUnfinishedPayment = function quoteIsUnfinishedPayment(info) {
  return [QuoteStatus.PAYMENT_PENDING, QuoteStatus.PAYMENT_PROCESSING, QuoteStatus.PURCHASE_FAILED, QuoteStatus.PRE_PAYMENT, QuoteStatus.DROPOUT_AFTER_SIGNING].includes(info === null || info === void 0 ? void 0 : info.status);
};
export var filterSortInsurerCoverageList = function filterSortInsurerCoverageList(coverageList, config) {
  var sortKeyList = [];
  var sortResultList = [];

  switch (config === null || config === void 0 ? void 0 : config.providerCode) {
    case PROVIDER_SOURCE().GREATEAMERICAN.value:
      var _getGreateAmericanCov = getGreateAmericanCoverMap(),
          prePowerUnit = _getGreateAmericanCov.prePowerUnit,
          deductible = _getGreateAmericanCov.deductible,
          reloading = _getGreateAmericanCov.reloading,
          debrisRemoval = _getGreateAmericanCov.debrisRemoval,
          earnedFreight = _getGreateAmericanCov.earnedFreight,
          fireDepartment = _getGreateAmericanCov.fireDepartment,
          lossData = _getGreateAmericanCov.lossData,
          rewardCoverage = _getGreateAmericanCov.rewardCoverage,
          pollutantCleanUp = _getGreateAmericanCov.pollutantCleanUp,
          refrigerationBreakdown = _getGreateAmericanCov.refrigerationBreakdown,
          ownersGoodsExtension = _getGreateAmericanCov.ownersGoodsExtension,
          nonOwnedTrailer = _getGreateAmericanCov.nonOwnedTrailer,
          nonOwnedTrailerLimit = _getGreateAmericanCov.nonOwnedTrailerLimit,
          reusablePackingContainers = _getGreateAmericanCov.reusablePackingContainers,
          reusablePackingContainersLimit = _getGreateAmericanCov.reusablePackingContainersLimit,
          tarpsChains = _getGreateAmericanCov.tarpsChains,
          tarpsChainsLimit = _getGreateAmericanCov.tarpsChainsLimit;

      sortKeyList = [prePowerUnit.value, deductible.value, reloading.value, debrisRemoval.value, earnedFreight.value, fireDepartment.value, lossData.value, rewardCoverage.value, pollutantCleanUp.value, refrigerationBreakdown.value, ownersGoodsExtension.value, nonOwnedTrailer.value, nonOwnedTrailerLimit.value, reusablePackingContainers.value, reusablePackingContainersLimit.value, tarpsChains.value, tarpsChainsLimit.value];
      break;

    case PROVIDER_SOURCE().ACCELERANT.value:
      sortKeyList = [PROFESSIONAL_INDEMNITY, FIDELITY_CRIME, PUBLIC_LIABILITY, EMPLOYERS_LIABILITY, DIRECTORS_OFFICERS_COVER, EMPLOYMENT_PRACTICES_COVER, COMPUTER_BREAKDOWN_COVER, MONEY_ASSAULT_COVER, PACKAGE_COVER, PROPERTY_COVER_TYPE_LIST, PORTABLE_EQUIPMENT_COVER, BUSINESS_INTERUPTION];
      break;

    case PROVIDER_SOURCE().RECRUITMENT.value:
      sortKeyList = [PROFESSIONAL_INDEMNITY, FIDELITY_CRIME, PUBLIC_LIABILITY, EMPLOYERS_LIABILITY, DIRECTORS_OFFICERS_COVER, DRIVERS_NEGLIGENCE, EMPLOYMENT_PRACTICES_COVER, COMPUTER_BREAKDOWN_COVER, MONEY_ASSAULT_COVER, PACKAGE_COVER, PROPERTY_COVER_TYPE_LIST, PORTABLE_EQUIPMENT_COVER, BUSINESS_INTERUPTION];
      break;

    case PROVIDER_SOURCE().ACCELERANT_GIT.value:
      sortKeyList = [GOODS_IN_TRANSIT, PUBLIC_LIABILITY, CMR_COVER];
      break;

    case PROVIDER_SOURCE().HISCOX_US.value:
      sortKeyList = [PROFESSIONAL_INDEMNITY, PUBLIC_LIABILITY, BUSINESS_OWNERS_POLICY_COVER, CYBER_LIABILITY_COVER];
      break;

    default:
      break;
  }

  var orderMap = new Map(sortKeyList.map(function (item, index) {
    return [item, index];
  }));
  sortResultList = orderBy(coverageList, [function (item) {
    return orderMap.has(item) ? orderMap.get(item) : Infinity;
  }], ['asc']);
  return sortResultList;
};
export var getPriceDisplayTextWithUnit = function getPriceDisplayTextWithUnit(value, unit, config) {
  if (unit === PromotionOfferType.PERCENTAGE) {
    return "".concat(multiplyNumber(value, 100), "%");
  } else {
    return config !== null && config !== void 0 && config.unitPrefix ? "".concat(config === null || config === void 0 ? void 0 : config.unitPrefix).concat(value) : formatCurrency({
      amount: value,
      currency: config === null || config === void 0 ? void 0 : config.currency
    });
  }
};
export var judgeIsBrokerQuote = function judgeIsBrokerQuote(data) {
  var _ref2 = data || {},
      promotionId = _ref2.promotionId;

  return !!promotionId;
};
export var getQuoteSourceDisplayText = function getQuoteSourceDisplayText(source, quoteData) {
  if (!source) {
    return '';
  }

  var _ref3 = quoteData || {},
      promotionId = _ref3.promotionId;

  var defaultQuoteText = 'Quote';
  var text = '';

  switch (source) {
    case QuoteSourceEnum.JOVE_CMS:
      text = !!promotionId ? getBrokerQuotingThroughMap().brokerPortal.label : defaultQuoteText;
      break;

    case QuoteSourceEnum.JOVE_QUOTE:
      text = !!promotionId ? getBrokerQuotingThroughMap().landingPage.label : defaultQuoteText;
      break;

    case QuoteSourceEnum.ACTURIS:
      text = getBrokerQuotingThroughMap().acturis.label;
      break;

    default:
      text = 'Quote';
      break;
  }

  return text;
};
export var getTheAssumedQuoteProvidercode = function getTheAssumedQuoteProvidercode(config) {
  var code = '';

  if ((config === null || config === void 0 ? void 0 : config.insuranceType) === InsuranceType.GOODS_IN_TRANSIT_INSURANCE) {
    if ([].concat(_toConsumableArray(UKCountryList), _toConsumableArray(EuropeanUnionList)).includes(config === null || config === void 0 ? void 0 : config.countryCode)) {
      code = PROVIDER_SOURCE().ACCELERANT_GIT.value;
    } else if (AmericanCountryList.includes(config === null || config === void 0 ? void 0 : config.countryCode)) {
      code = PROVIDER_SOURCE().GREATEAMERICAN.value;
    }
  } else if ((config === null || config === void 0 ? void 0 : config.insuranceType) === InsuranceType.BUSINESS_INSURANCE) {
    if ([].concat(_toConsumableArray(UKCountryList), _toConsumableArray(EuropeanUnionList)).includes(config === null || config === void 0 ? void 0 : config.countryCode)) {
      code = PROVIDER_SOURCE().ACCELERANT.value;
    } else if (AmericanCountryList.includes(config === null || config === void 0 ? void 0 : config.countryCode)) {
      code = PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value;
    }
  }

  return code;
};